/**
 * Fleet API service.
 *
 * Vehicles the shop owns (courtesy cars, company cars, work vehicles), the
 * bookings against them, and the service/compliance event log.
 *
 * Kept out of api.js because that file already carries the whole quoting
 * domain; fleet is self-contained and reads better on its own.
 */

import appwrite, {
  databases,
  storage,
  DATABASE_ID,
  COLLECTIONS,
  BUCKETS,
  Query,
  ID,
  Permission,
  Role,
  getCurrentUser
} from './appwrite';
import { softDelete } from './records';
import { notDeleted } from '../lib/records';

// Appwrite caps a page at 100 rows. A fleet is small, but bookings accumulate,
// so anything unbounded pages until it has the lot.
const PAGE_SIZE = 100;

async function listAll(collectionId, queries = []) {
  const rows = [];
  let cursor = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = [...queries, Query.limit(PAGE_SIZE)];
    if (cursor) page.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(DATABASE_ID, collectionId, page);
    rows.push(...response.documents);
    if (response.documents.length < PAGE_SIZE) break;
    cursor = response.documents[response.documents.length - 1].$id;
  }

  return rows;
}

/** Strip Appwrite system fields and expanded relations before writing back. */
function writable(data, relationKeys = []) {
  const out = {};
  Object.entries(data || {}).forEach(([key, value]) => {
    if (key.startsWith('$')) return;
    if (value === undefined) return;
    // Relationships are set by id; an expanded row would be rejected.
    if (relationKeys.includes(key) && value && typeof value === 'object') {
      out[key] = value.$id ?? null;
      return;
    }
    out[key] = value;
  });
  return out;
}

const VEHICLE_RELATIONS = [];
const BOOKING_RELATIONS = ['vehicle', 'customer', 'quoteRequest', 'job'];
const EVENT_RELATIONS = ['vehicle', 'booking'];

// =====================================================
// VEHICLES
// =====================================================

export async function fleetVehicleList() {
  return listAll(COLLECTIONS.FLEET_VEHICLES, [notDeleted(), Query.orderAsc('sortOrder'), Query.orderAsc('registration')]);
}

export async function fleetVehicleGet(id) {
  return databases.getDocument(DATABASE_ID, COLLECTIONS.FLEET_VEHICLES, id);
}

export async function fleetVehicleCreate(data) {
  const user = await getCurrentUser();
  return appwrite.createDocument(COLLECTIONS.FLEET_VEHICLES, {
    ...writable(data, VEHICLE_RELATIONS),
    createdBy: user?.id || null
  });
}

export async function fleetVehicleUpdate(id, data) {
  const user = await getCurrentUser();
  return appwrite.updateDocument(COLLECTIONS.FLEET_VEHICLES, id, {
    ...writable(data, VEHICLE_RELATIONS),
    updatedBy: user?.id || null
  });
}

/** Soft: restorable for 90 days. Refused by the form while the vehicle has bookings. */
export async function fleetVehicleDelete(vehicleOrId) {
  return softDelete(COLLECTIONS.FLEET_VEHICLES, vehicleOrId);
}

/** Persist a drag-reorder of the vehicle list. */
export async function fleetVehicleReorder(orderedVehicles) {
  return Promise.all(
    orderedVehicles.map((vehicle, index) =>
      vehicle.sortOrder === index
        ? Promise.resolve(vehicle)
        : appwrite.updateDocument(COLLECTIONS.FLEET_VEHICLES, vehicle.$id, { sortOrder: index })
    )
  );
}

/**
 * Record a new odometer reading, ignoring anything lower than what we already
 * hold — readings arrive from handovers and returns out of order, and a car's
 * odometer only goes up.
 */
export async function fleetVehicleRecordOdometer(vehicle, reading) {
  const km = Number(reading);
  if (!Number.isFinite(km) || km <= 0) return vehicle;
  if (Number(vehicle.odometer) >= km) return vehicle;
  return fleetVehicleUpdate(vehicle.$id, { odometer: km, odometerUpdatedAt: new Date().toISOString() });
}

// =====================================================
// BOOKINGS
// =====================================================

/**
 * Bookings overlapping a window, plus anything still open.
 *
 * The second query catches cars that went out before the window and have not
 * come back — they still occupy their vehicle today, so leaving them out would
 * show the car as free.
 *
 * Every query leaves deleted bookings out: one somebody deleted must stop
 * holding its vehicle.
 */
export async function fleetBookingList({ from, to } = {}) {
  if (!from || !to) {
    return listAll(COLLECTIONS.FLEET_BOOKINGS, [notDeleted(), Query.orderAsc('scheduledStart')]);
  }

  const fromIso = new Date(from).toISOString();
  const toIso = new Date(to).toISOString();

  const [inWindow, stillOut, openEnded] = await Promise.all([
    listAll(COLLECTIONS.FLEET_BOOKINGS, [
      notDeleted(),
      Query.lessThanEqual('scheduledStart', toIso),
      Query.greaterThanEqual('scheduledEnd', fromIso),
      Query.orderAsc('scheduledStart')
    ]),
    listAll(COLLECTIONS.FLEET_BOOKINGS, [
      notDeleted(),
      Query.equal('status', 'out'),
      Query.lessThan('scheduledEnd', fromIso),
      Query.orderAsc('scheduledStart')
    ]),
    // No return date at all — runs from its start until someone ends it, so it
    // covers the window whenever it began before the window closes.
    listAll(COLLECTIONS.FLEET_BOOKINGS, [
      notDeleted(),
      Query.isNull('scheduledEnd'),
      Query.lessThanEqual('scheduledStart', toIso),
      Query.orderAsc('scheduledStart')
    ])
  ]);

  const seen = new Set();
  return [...inWindow, ...stillOut, ...openEnded].filter((booking) => {
    if (seen.has(booking.$id)) return false;
    seen.add(booking.$id);
    return true;
  });
}

export async function fleetBookingGet(id) {
  return databases.getDocument(DATABASE_ID, COLLECTIONS.FLEET_BOOKINGS, id);
}

/** Every booking for one vehicle, newest first. */
export async function fleetBookingsForVehicle(vehicleId) {
  try {
    return await listAll(COLLECTIONS.FLEET_BOOKINGS, [
      Query.equal('vehicle', vehicleId),
      notDeleted(),
      Query.orderDesc('scheduledStart')
    ]);
  } catch {
    // Filtering on a relationship column is not indexable on every Appwrite
    // version; fall back to filtering the recent set client-side.
    const all = await listAll(COLLECTIONS.FLEET_BOOKINGS, [notDeleted(), Query.orderDesc('scheduledStart')]);
    return all.filter((b) => (typeof b.vehicle === 'string' ? b.vehicle : b.vehicle?.$id) === vehicleId);
  }
}

export async function fleetBookingCreate(data) {
  const user = await getCurrentUser();
  return appwrite.createDocument(COLLECTIONS.FLEET_BOOKINGS, {
    ...writable(data, BOOKING_RELATIONS),
    createdBy: user?.id || null
  });
}

export async function fleetBookingUpdate(id, data) {
  const user = await getCurrentUser();
  return appwrite.updateDocument(COLLECTIONS.FLEET_BOOKINGS, id, {
    ...writable(data, BOOKING_RELATIONS),
    updatedBy: user?.id || null
  });
}

/** Soft: restorable for 90 days, and no longer holding its vehicle. */
export async function fleetBookingDelete(bookingOrId) {
  return softDelete(COLLECTIONS.FLEET_BOOKINGS, bookingOrId);
}

/**
 * Hand a vehicle over: stamp the real collection time, take the odometer, and
 * push that reading onto the vehicle so service and RUC stay honest.
 */
export async function fleetBookingHandOver(booking, details = {}) {
  const user = await getCurrentUser();
  const now = new Date().toISOString();

  const updated = await fleetBookingUpdate(booking.$id, {
    status: 'out',
    actualStart: details.actualStart || now,
    odometerOut: details.odometerOut ?? null,
    fuelLevelOut: details.fuelLevelOut ?? null,
    conditionOut: details.conditionOut ?? null,
    handedOverBy: user?.id || null,
    handedOverAt: now,
    ...details.extra
  });

  const vehicle = typeof booking.vehicle === 'object' ? booking.vehicle : null;
  if (vehicle && details.odometerOut) {
    await fleetVehicleRecordOdometer(vehicle, details.odometerOut);
  }

  return updated;
}

/**
 * Take a vehicle back. New damage is logged as an event so it shows in the
 * vehicle's history rather than only on the booking that caused it.
 */
export async function fleetBookingReturn(booking, details = {}) {
  const user = await getCurrentUser();
  const now = new Date().toISOString();

  const updated = await fleetBookingUpdate(booking.$id, {
    status: 'returned',
    actualEnd: details.actualEnd || now,
    odometerIn: details.odometerIn ?? null,
    fuelLevelIn: details.fuelLevelIn ?? null,
    conditionIn: details.conditionIn ?? null,
    newDamageReported: Boolean(details.newDamageReported),
    returnedTo: user?.id || null,
    returnedAt: now,
    totalCharged: details.totalCharged ?? booking.totalCharged ?? null,
    ...details.extra
  });

  const vehicleId = typeof booking.vehicle === 'string' ? booking.vehicle : booking.vehicle?.$id;

  if (details.odometerIn && typeof booking.vehicle === 'object' && booking.vehicle) {
    await fleetVehicleRecordOdometer(booking.vehicle, details.odometerIn);
  }

  if (details.newDamageReported && vehicleId) {
    await fleetEventCreate({
      vehicle: vehicleId,
      booking: booking.$id,
      type: 'damage',
      occurredAt: now,
      odometer: details.odometerIn ?? null,
      description: details.damageDescription || 'New damage recorded on return'
    });
  }

  return updated;
}

// =====================================================
// VEHICLE EVENTS (service, WoF, rego, damage, notes)
// =====================================================

export async function fleetEventList(vehicleId) {
  const queries = [Query.orderDesc('occurredAt')];
  if (vehicleId) {
    try {
      return await listAll(COLLECTIONS.FLEET_VEHICLE_EVENTS, [Query.equal('vehicle', vehicleId), ...queries]);
    } catch {
      const all = await listAll(COLLECTIONS.FLEET_VEHICLE_EVENTS, queries);
      return all.filter((e) => (typeof e.vehicle === 'string' ? e.vehicle : e.vehicle?.$id) === vehicleId);
    }
  }
  return listAll(COLLECTIONS.FLEET_VEHICLE_EVENTS, queries);
}

export async function fleetEventCreate(data) {
  const user = await getCurrentUser();
  return appwrite.createDocument(COLLECTIONS.FLEET_VEHICLE_EVENTS, {
    ...writable(data, EVENT_RELATIONS),
    occurredAt: data.occurredAt || new Date().toISOString(),
    createdBy: user?.id || null
  });
}

export async function fleetEventDelete(id) {
  return appwrite.deleteDocument(COLLECTIONS.FLEET_VEHICLE_EVENTS, id);
}

/**
 * Log a completed compliance job and roll the vehicle's expiry forward in one
 * step, so "new WoF done" is a single action rather than an edit plus a note.
 */
export async function fleetRecordCompliance(vehicle, { type, occurredAt, expiresAt, expiryOdometer, odometer, cost, supplier, description }) {
  await fleetEventCreate({
    vehicle: vehicle.$id,
    type,
    occurredAt,
    expiresAt: expiresAt || null,
    expiryOdometer: expiryOdometer ?? null,
    odometer: odometer ?? null,
    cost: cost ?? null,
    supplier: supplier || null,
    description: description || null
  });

  const patch = {};
  // WoF and CoF share one expiry — a vehicle needs one or the other, never both.
  if ((type === 'wof' || type === 'cof') && expiresAt) patch.wofExpiryDate = expiresAt;
  if (type === 'rego' && expiresAt) patch.regoExpiryDate = expiresAt;
  if (type === 'ruc' && expiryOdometer) patch.rucExpiryOdometer = Number(expiryOdometer);
  if (type === 'service') {
    if (occurredAt) patch.lastServiceDate = occurredAt;
    if (odometer) patch.lastServiceOdometer = Number(odometer);
    if (expiresAt) patch.serviceDueDate = expiresAt;
    if (expiryOdometer) patch.serviceDueOdometer = Number(expiryOdometer);
  }
  if (odometer && Number(odometer) > Number(vehicle.odometer || 0)) {
    patch.odometer = Number(odometer);
    patch.odometerUpdatedAt = new Date().toISOString();
  }

  return Object.keys(patch).length ? fleetVehicleUpdate(vehicle.$id, patch) : vehicle;
}

// =====================================================
// FILES (condition photos, licence scans, signed agreements)
// =====================================================

export async function fleetUploadFile(file) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const fileId = ID.unique();
  await storage.createFile(BUCKETS.FLEET_DOCUMENTS, fileId, file, [
    // Only roles the uploader holds may be granted — Appwrite rejects the
    // upload otherwise. Every member holds Role.team(teamId).
    Permission.read(Role.team(user.teamId)),
    Permission.update(Role.team(user.teamId)),
    Permission.delete(Role.team(user.teamId))
  ]);

  return fileId;
}

export async function fleetDeleteFile(fileId) {
  try {
    await storage.deleteFile(BUCKETS.FLEET_DOCUMENTS, fileId);
  } catch (err) {
    // The record no longer referencing the file is what matters to the app.
    console.warn('Could not delete fleet file', fileId, err?.message);
  }
}

/** Turn a data URL (signature canvas output) into an uploadable file. */
export async function fleetUploadDataUrl(dataUrl, filename = 'signature.png') {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return fleetUploadFile(new File([blob], filename, { type: blob.type || 'image/png' }));
}

// =====================================================
// CONDITION REPORTS
// =====================================================
// Stored as JSON on the booking rather than in their own table — a handover
// carries a handful of marks and photos, and keeping it inline means the whole
// report arrives with the booking in one read.
//
// conditionOut/conditionIn/notes are declared at size 20000 in
// appwrite.config.json, and that number is load-bearing. Appwrite stores a
// string as VARCHAR(size) costing size * 4 bytes against a 65,535-byte row
// limit, switching to TEXT (2 bytes) only above 16383. At 8000 these two
// columns alone cost 64,000 and the table would not deploy:
//   "The maximum number or size of attributes for collection has been reached."
// Keep any long-text column on these tables above 16383 for the same reason.

export const EMPTY_CONDITION = { zones: [], photos: [], notes: '' };

export function parseCondition(value) {
  if (!value) return { ...EMPTY_CONDITION };
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return {
      zones: Array.isArray(parsed.zones) ? parsed.zones : [],
      photos: Array.isArray(parsed.photos) ? parsed.photos : [],
      notes: parsed.notes || ''
    };
  } catch {
    return { ...EMPTY_CONDITION };
  }
}

export function serialiseCondition(condition) {
  const safe = {
    zones: condition?.zones || [],
    photos: condition?.photos || [],
    notes: condition?.notes || ''
  };
  return JSON.stringify(safe);
}

export const fleetApi = {
  fleetVehicleList,
  fleetVehicleGet,
  fleetVehicleCreate,
  fleetVehicleUpdate,
  fleetVehicleDelete,
  fleetVehicleReorder,
  fleetVehicleRecordOdometer,
  fleetBookingList,
  fleetBookingGet,
  fleetBookingsForVehicle,
  fleetBookingCreate,
  fleetBookingUpdate,
  fleetBookingDelete,
  fleetBookingHandOver,
  fleetBookingReturn,
  fleetEventList,
  fleetEventCreate,
  fleetEventDelete,
  fleetRecordCompliance,
  fleetUploadFile,
  fleetDeleteFile,
  fleetUploadDataUrl
};

export default fleetApi;
